import {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  ChangeEvent,
  MutableRefObject,
} from 'react'
import { useHistory, useLocation } from 'react-router'
import styled from 'styled-components'
import debounce from 'lodash/debounce'
import { v4 as uuid } from 'uuid'

import useDispatch from 'hooks/useDispatch'
import useDocumentClick from 'hooks/useDocumentClick'
import { useGoogleMapsScriptReady } from 'hooks/useGoogleMapsScriptLoadedRef'
import { useGetSearchBarResultsLazyQuery } from 'hooks/useQueries'
import { searchTypes, updateFilters } from 'actions/searchActions'
import { deserializeSearchQuery } from 'actions/utils/search'

import Input from './Input'
import Results from './Results'

type LocationFilters = { country: string; state?: string }

interface SearchBarProps {
  mobile?: boolean
}

const Wrapper = styled.div<{ $loading?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  position: relative;
  border-radius: 6px;

  background-color: ${({ $loading, theme }) => {
    if ($loading) {
      return theme['-ds-neutral-300'].value
    }

    return theme['-ds-neutral-100'].value
  }};
`

const useGoogleLoading = () => !useGoogleMapsScriptReady()

const useFocusInputOnOpen = (
  searchInputRef: MutableRefObject<HTMLInputElement>,
  open: boolean
) => {
  useEffect(() => {
    if (open) {
      searchInputRef.current?.focus()
    }
  }, [open, searchInputRef])
}

type LocationState = {
  primaryCityName: string
}

interface SearchBarProps {
  mobile?: boolean
}

const SearchBar = ({ mobile }: SearchBarProps) => {
  const dispatch = useDispatch()
  const location = useLocation<LocationState>()
  const history = useHistory()

  const queryStringFilters = deserializeSearchQuery(location.search)

  // SSR has Maps unloaded → empty wrapper; client commonly has Maps already
  // loaded by hydrateRoot time → full wrapper. Force the first client render
  // to match SSR by gating reveal on a post-mount flag (Rollbar #16554).
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    setHydrated(true)
  }, [])

  const [open, setOpen] = useState(false)
  const [filters, setFilters] = useState(queryStringFilters)
  const [query, setQuery] = useState('')

  const debouncedEvent = useCallback(
    debounce((data, _query) => {
      analytics.track('Search by Title', {
        _query,
        resultsCount: data.searchByTitle?.edges?.length,
      })
    }, 1500),
    []
  )

  const [sessionToken, setSessionToken] = useState(null)

  useEffect(() => {
    setSessionToken(uuid())
  }, [])

  const [
    callSearchBarResults,
    { data: searchBarResults, loading: searchBarResultsLoading },
  ] = useGetSearchBarResultsLazyQuery()

  const combinedResults = searchBarResults?.searchByTitle || []

  const googleLoading = useGoogleLoading()

  const searchInputRef = useRef<HTMLInputElement>()

  const outsideClickBoundary = useRef<HTMLDivElement>()

  const fetchAllResults = useMemo(
    () =>
      debounce((value: string): void => {
        callSearchBarResults({
          variables: { query: value, sessionToken },
        })
          .then(({ data }) => {
            if (data) debouncedEvent(data, value)
          })
          .catch((): void => {
            /* ignore search-bar lookup failures; they are non-critical */
          })
      }, 300),
    [sessionToken]
  )

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target

    setQuery(value || '')
    fetchAllResults(value)
  }

  const onRestoreFilters = () => {
    setFilters(queryStringFilters)
  }

  const onOpen = () => {
    setOpen(true)
  }

  const onClose = () => {
    setOpen(false)
  }

  const onClear = () => {
    setQuery('')
    onRestoreFilters()
    searchInputRef.current?.focus()
  }

  const onShortcutSelect = ({ country, state = 'all' }: LocationFilters) => {
    history.push(
      `/listings?type=accommodation&country=${country}&state=${state}`
    )

    dispatch(
      updateFilters({
        ne: null,
        sw: null,
      })
    )

    onClose()
  }

  const inputPlaceholder = () => {
    const activeCountry = filters?.country

    const activeState = filters.state

    const activeType = searchTypes.find((type) => type.slug === filters.type)

    let placeholder = 'Search location or title'

    if ((activeType || activeState || activeCountry) && !open && !filters.q) {
      if (filters?.location) {
        placeholder = filters.location.toString()
      } else if (activeState) {
        placeholder = activeState.toString()
      } else if (location.state?.primaryCityName) {
        placeholder = `Near ${location.state.primaryCityName}`
      } else if (activeCountry) {
        placeholder = `All ${activeCountry}`
      } else {
        placeholder = 'Anywhere'
      }
    }

    return placeholder
  }

  const closeOnOutsideClick = (e: Event) => {
    if (
      outsideClickBoundary.current &&
      !outsideClickBoundary.current.contains(e.target as Node)
    ) {
      onClose()
    }
  }

  useDocumentClick(closeOnOutsideClick)

  useFocusInputOnOpen(searchInputRef, open)

  useEffect(() => {
    const newFilters = deserializeSearchQuery(location.search)
    setFilters(newFilters)
  }, [location.search])

  const showLoadingWrapper = !hydrated || googleLoading
  if (showLoadingWrapper) {
    return <Wrapper $loading={showLoadingWrapper} />
  }

  return (
    <Wrapper $loading={googleLoading} ref={outsideClickBoundary}>
      <Input
        open={open}
        mobile={mobile}
        query={query}
        onChange={onChange}
        onClear={onClear}
        onClose={onClose}
        onOpen={onOpen}
        searchInputRef={searchInputRef}
        placeholder={inputPlaceholder()}
      />

      {open && (
        <Results
          mobile={mobile}
          query={query}
          results={combinedResults}
          loading={searchBarResultsLoading}
          onShortcutSelect={onShortcutSelect}
          onClose={onClose}
          sessionToken={sessionToken}
        />
      )}
    </Wrapper>
  )
}

export default SearchBar
